import React, { useState, useEffect } from 'react';
import Header from './Header'; // Import Header component
import Sidebar from './Sidebar'; // Import Sidebar component
import './PatientHistory.css'; // Ensure you create this CSS file for styling
import './Dashboard.css'; // Ensure you create this CSS file for styling
import { faChevronRight, faTrash, faFileCsv } from '@fortawesome/free-solid-svg-icons'; // Import right arrow icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {axiosInstance} from './helper/axiosConfig';
import { Link, useLocation } from 'react-router-dom'; // Import Link and useLocation for navigatio
import { isAdmin, isDoctor } from './helper/authRoles';

// Function to generate random colors for avatars
const getRandomColor = () => {
  const colors = ['#f56565', '#68d391', '#63b3ed', '#ed8936', '#f6ad55', '#9f7aea', '#48bb78', '#4299e1'];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Utility function to get the first letter of the patient's name
const getAvatarLetter = (name) => {
  return name ? name.charAt(0).toUpperCase() : '?';
};

function PatientHistory() {
  const [patientHistory, setPatientHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // Set rows per page for pagination
  const [totalPages, setTotalPages] = useState(1); // Track total pages
  const [searchTerm, setSearchTerm] = useState(''); // State for search term

  const currentDate = new Date();
  const tenYearsAgo = new Date(currentDate.setFullYear(currentDate.getFullYear() - 10)); // Calculate the date 10 years ago

  const [dateRange, setDateRange] = useState({
    startDate: tenYearsAgo,
    endDate: new Date(),
  });

  // Fetch patient history data with pagination and filtering
  const fetchPatientHistory = async (page, searchTerm, dateRange) => {
    try {
      const response = await axiosInstance.get(`/api/patient-all/`, {
        params: {
          page: page, // Pass the current page
          limit: rowsPerPage, // Pass the rows per page
          search: searchTerm, // Pass the search term
          startDate: dateRange.startDate.toISOString(), // Pass the start date
          endDate: dateRange.endDate.toISOString(), // Pass the end date
        },
      });
      setPatientHistory(response.data.results); // Assuming the API returns a `results` array
      setTotalPages(Math.ceil(response.data.total / rowsPerPage)); // Assuming the API returns a `total` count of patients
    } catch (error) {
      console.error('Error fetching patient history:', error);
      setError('Failed to fetch patient history.');
    } finally {
      setLoading(false); // Stop the loading state
    }
  };

  const exportToCSV = () => {
    if (!patientHistory || patientHistory.length === 0) {
      alert('No data available to export.');
      return;
    }

    const headers = [
      'Patient ',
      'Age',
      "Patient Type",
      'Gender',
      'Marital Status',
      'Address',
    ];



    const rows = patientHistory.map((patient) => [
      patient.name,
      patient.age + ' ' +patient.age_type,
      patient.has_prescription  ? 'Old' : 'New',
      patient.gender,
      patient.marital_status,
      patient.address_district+'_'+patient.address_thana,
    ]);

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'patient_history.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  useEffect(() => {
    fetchPatientHistory(currentPage, searchTerm, dateRange);
  }, [currentPage, searchTerm, dateRange]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  const handleDeletePatient = async (patientId) => {
    const r = window.confirm("Are you sure to remove the patient?");
    if(r){
      try {
        await axiosInstance.delete(`/api/patient/${patientId}`);
        // Handle successful deletion, e.g., update the list of appointments, show a success message
        console.log('Patient deleted successfully');
        fetchPatientHistory(currentPage, searchTerm, dateRange);
        //history('/appoinment-history'); // Redirect after successful creation
      } catch (error) {
        console.error('Error deleting patient:', error);
        // Handle error, e.g., show an error message to the user
      }
    }
  };

  // New function to handle search and date changes
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleDateChange = (ranges) => {
    setDateRange({
      startDate: ranges.startDate,
      endDate: ranges.endDate,
    });
    setCurrentPage(1); // Reset to first page on date change
  };

  return (
    <div className="dashboard-container">
      <Sidebar /> {/* Sidebar component */}
      <div className="dashboard-main">
      <Header onSearch={handleSearch} onDateChange={handleDateChange} /> {/* Pass handlers to Header */}
        <div className="content-box">
          <div className="patient-history-container">
          <div className="header-container">
            <>
              <h1>Patient History</h1>
              <div>
              {isAdmin() && (
                <button className="btn print-btn">
                  <Link to="/create-patient/"> + Add Patient</Link>
                </button>
              )}
              {isAdmin() && (
              <button onClick={exportToCSV} className="export-button">
                 <FontAwesomeIcon icon={faFileCsv} className="picker-icon" /> Export CSV
              </button>
              )}
              </div>
            </>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <>
              {patientHistory && patientHistory.length > 0 && (
                <>
                <table className="patient-history-table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Age</th>
                      <th>Patient Type</th>
                      <th>Gender</th>
                      <th>Marital Status</th>
                      <th>Address</th>
                      <th>&nbsp;</th>
                    {/*  <th>&nbsp;</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {patientHistory.map((patient) => (
                      <tr key={patient.id}>
                        <td>
                          {/* Avatar circle
                          // <div
                          //   className="avatar-circle"
                          //   style={{ backgroundColor: getRandomColor() }}
                          // >
                          //   <span className="initials">{getAvatarLetter(patient.name)}</span>
                          // </div>
                          */}
                          <div className="avatar-circle" style={{ backgroundColor: !patient.profile_pic ? getRandomColor() : 'transparent' }}>
                            {patient.profile_pic ? (
                              <img
                                    src={`${process.env.REACT_APP_AXIOS_BASE_URL}${patient.profile_pic}`} // Concatenating the base URL and profile_pic
                                    alt={patient.full_name} className="avatar-circle"
                                  // Ensure the profile-pic class is styled in your CSS
                                  />

                            ) : (
                              <span className="initials">{getAvatarLetter(patient.name)}</span>
                            )}
                          </div>

                            {patient.name}
                        </td>
                        <td>{patient.age} {patient.age_type}</td>
                        <td>
                            {patient.has_prescription  ? 'Old' : 'New'}
                        </td>
                        <td>{patient.gender}</td>
                        <td>{patient.marital_status}</td>
                        <td>
                          {patient.address_district}
                          {patient.address_district && patient.address_thana ? ', ' : ''}
                          {patient.address_thana}
                        </td>
                        <td>
                          <Link to={`/patient-details/${patient.id}`}>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="picker-icon"
                            />
                          </Link>
                        </td>
                        {/*
                          <td>{patient.has_prescription}
                              { !patient.has_prescription && ( // Call the function here
                                  <a onClick={() => handleDeletePatient(patient.id)} className="trash-icon">
                                    <FontAwesomeIcon icon={faTrash} className="picker-icon" />
                                  </a>
                              )}
                          </td>
                          */}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                {totalPages > rowsPerPage && (
                  <>
                    {currentPage > 1 && (
                      <button onClick={() => handlePageChange(currentPage - 1)} className="ltgt">&lt;</button>
                    )}
                    {Array.from({ length: totalPages }, (_, index) => {
                      if (index + 1 === currentPage || index + 1 === 1 || index + 1 === totalPages || Math.abs(currentPage - (index + 1)) <= 1) {
                        return (
                          <button
                            key={index + 1}
                            onClick={() => handlePageChange(index + 1)}
                            className={currentPage === index + 1 ? 'active' : ''}
                          >
                            {index + 1}
                          </button>
                        );
                      } else if (Math.abs(currentPage - (index + 1)) === 2) {
                        return <span key={index} className="dots">...</span>;
                      }
                      return null;
                    })}
                    {currentPage < totalPages && (
                      <button onClick={() => handlePageChange(currentPage + 1)} className="ltgt">&gt;</button>
                    )}
                  </>
                )}
                </div>
                </>
              )}
              {patientHistory && patientHistory.length === 0 && (
                <p>No patient's found.</p>
              )}
              </>
            )}
          </div>
        </div>
        <div className="footer-content">
            {process.env.REACT_APP_COPYRIGHT_TEXT || "© 2025, Powered by AmarLab"}
        </div>
      </div>
    </div>
  );
}

export default PatientHistory;
